import React, { useCallback, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Grid } from '@material-ui/core'
import AdminPage from '../../../admin/shared/AdminPage'
import Loading from '../../../../molecules/Loading'
import Card from '../../../../molecules/Card'
import FlexRow from '../../../../molecules/FlexRow'
import NotAvailable from '../../../../organisms/NotAvailable'
import SydButton from '../../../../commonDesign/Button'
import SydActionMenu from '../../../../commonDesign/SydActionMenu'
import { provisionedTargets } from '../../../../../api/integrations'
import { useDenaliContext } from '../../DenaliProvider'
import { SUCCESS_ALERT } from '../../../../../constants'
import { decipherError } from '../../../../../utils/decipherError'
import {
  PartitionKeyCard,
  DomainKeyCard,
  ColumnsCard,
  IndexesCard,
  AssociationsCard,
  ChangeTrackingCard
} from './definition'
import CheckMigrationsModal from './CheckMigrationsModal'
import AssignJobDialog from './AssignJobDialog'
import UnassignJobDialog from './UnassignJobDialog'
import EditDetailsModal from './EditDetailsModal'

function ProvisionedTargetDetails () {
  const { provisionedTargetTableId } = useParams()
  const targetId = useMemo(() => Number.parseInt(provisionedTargetTableId), [provisionedTargetTableId])
  const denaliContext = useDenaliContext()

  const { data: targetData, isLoading: targetLoading } = provisionedTargets.useGetProvisionedTargetTableById(targetId)
  const { data: partitionsData, isLoading: partitionsLoading } = provisionedTargets.useListProvisionedTargetPartitions(targetId)
  const { data: jobsData, isLoading: jobsLoading } = provisionedTargets.useListJobsByProvisionedTarget(targetId)
  const { mutateAsync: modifyTarget } = provisionedTargets.useModifyProvisionedTargetTable()

  const target = targetData?.result
  const partitions = partitionsData?.result || []
  const jobs = jobsData?.result || []

  const [editDetailsOpen, setEditDetailsOpen] = useState(false)
  const [checkMigrationsOpen, setCheckMigrationsOpen] = useState(false)
  const [assignJobOpen, setAssignJobOpen] = useState(false)
  const [unassignJob, setUnassignJob] = useState(null)

  const handleToggleLock = useCallback(async () => {
    try {
      await modifyTarget({
        provisionedTargetTableId: target.provisionedTargetTableId,
        isLocked: !target.isLocked
      })
      denaliContext.setAlert({
        ...SUCCESS_ALERT,
        alertMessage: target.isLocked ? 'Target unlocked' : 'Target locked'
      })
    } catch (err) {
      denaliContext.setAlert({ severity: 'error', alertMessage: decipherError(err) })
    }
  }, [modifyTarget, target, denaliContext])

  const actions = useMemo(() => {
    if (!target) return []
    const items = []
    items.push({ key: 'edit-details', label: 'Edit Details', icon: 'edit', onClick: () => setEditDetailsOpen(true) })
    items.push({
      key: 'toggle-lock',
      label: target.isLocked ? 'Unlock' : 'Lock',
      icon: target.isLocked ? 'lock_open' : 'lock',
      onClick: handleToggleLock
    })
    items.push({ key: 'check-migrations', label: 'Check Migrations', icon: 'database', onClick: () => setCheckMigrationsOpen(true) })
    return items
  }, [target, handleToggleLock])

  if (targetLoading || partitionsLoading || jobsLoading) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  if (!target) {
    return (
      <AdminPage>
        <NotAvailable />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <EditDetailsModal
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        target={target}
      />
      <CheckMigrationsModal
        open={checkMigrationsOpen}
        onClose={() => setCheckMigrationsOpen(false)}
        target={target}
      />
      <AssignJobDialog
        open={assignJobOpen}
        onClose={() => setAssignJobOpen(false)}
        target={target}
      />
      <UnassignJobDialog
        open={!!unassignJob}
        onClose={() => setUnassignJob(null)}
        job={unassignJob}
      />

      <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12}>
          <Card variant='clear'>
            <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>{target.tableName}</h2>
              <SydActionMenu actions={actions} />
            </FlexRow>
          </Card>
        </Grid>

        {/* Left column */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <h3>Details</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>Table Name</td>
                      <td>{target.tableName}</td>
                    </tr>
                    <tr>
                      <td>Domain</td>
                      <td>{target.domain || '—'}</td>
                    </tr>
                    <tr>
                      <td>Description</td>
                      <td>{target.description || '—'}</td>
                    </tr>
                    <tr>
                      <td>Locked</td>
                      <td>{target.isLocked ? 'Yes' : 'No'}</td>
                    </tr>
                    <tr>
                      <td>Definition Hash</td>
                      <td><code>{target.definitionHash}</code></td>
                    </tr>
                  </tbody>
                </table>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <PartitionKeyCard target={target} />
            </Grid>

            <Grid item xs={12}>
              <DomainKeyCard target={target} />
            </Grid>

            <Grid item xs={12}>
              <ChangeTrackingCard target={target} />
            </Grid>

            <Grid item xs={12}>
              <Card>
                <h3>Partitions ({partitions.length})</h3>
                <table className='opal-table'>
                  <thead>
                    <tr>
                      <th>Partition</th>
                      <th>Connection ID</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partitions.map(p => (
                      <tr key={p.provisionedTargetPartitionId}>
                        <td>{p.partitionName}</td>
                        <td>{p.connectionId}</td>
                        <td>{p.createdDate}</td>
                      </tr>
                    ))}
                    {partitions.length === 0 && (
                      <tr>
                        <td colSpan={3}>No partitions</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ColumnsCard target={target} />
            </Grid>

            <Grid item xs={12}>
              <IndexesCard target={target} />
            </Grid>

            <Grid item xs={12}>
              <AssociationsCard target={target} />
            </Grid>

            <Grid item xs={12}>
              <Card>
                <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Assigned Jobs ({jobs.length})</h3>
                  <SydButton variant='outline' size='sm' icon='add' onClick={() => setAssignJobOpen(true)}>
                    Assign Job
                  </SydButton>
                </FlexRow>
                <table className='opal-table'>
                  <thead>
                    <tr>
                      <th>Job Key</th>
                      <th>Domain</th>
                      <th>Connection ID</th>
                      <th>Enabled</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr key={j.jobConfigurationId}>
                        <td>
                          <Link to={`/admin/denali/integrator/jobs/${j.jobConfigurationId}`}>
                            {j.jobKey}
                          </Link>
                        </td>
                        <td>{j.domain}</td>
                        <td>{j.connectionId}</td>
                        <td>{j.isEnabled ? 'Yes' : 'No'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <SydButton
                            variant='ghost'
                            size='sm'
                            icon='link_off'
                            onClick={() => setUnassignJob(j)}
                          >
                            Unassign
                          </SydButton>
                        </td>
                      </tr>
                    ))}
                    {jobs.length === 0 && (
                      <tr>
                        <td colSpan={5}>No jobs assigned</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AdminPage>
  )
}

export default ProvisionedTargetDetails
